import moment from 'moment'
import { FC, useState } from 'react'
import { ReactComponent as TrendUpIcon } from 'src/assets/svg/trend-up.svg'
import { ReactComponent as TripadvisorIcon } from 'src/assets/svg/tripadvisor.svg'
import { ReactComponent as WarningTriangleIcon } from 'src/assets/svg/warning.svg'
import { Skeleton } from 'src/components/Skeleton'
import { cn } from 'src/utils/cn'
import { SpikeBreakdownModal } from 'src/pages/ReviewAnalysis/SpikeBreakdownModal'
import {
  FacilityActivityAlert,
  FacilityActivitySpike,
  FacilityPlatformActivity,
  FacilityVelocityItem,
} from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import styles from './reviewVelocity.module.scss'

// From reviewVelocity.module.scss: .velocityCard / .platformCard / .spikeCard
// are each 240x128 with radius 13, and an .alertItem is 298x40 with radius 12.
// Both containers are min-height 226, so they grow with content — the callers
// previously rendered this whole section only once `activity` existed, so it
// popped into the page and pushed everything below it down.
const CARD_WIDTH = 240
const CARD_HEIGHT = 128
const CARD_RADIUS = 13
const ALERT_WIDTH = 298
const ALERT_HEIGHT = 40
const ALERT_RADIUS = 12
const CARD_SKELETON_COUNT = 3
const ALERT_SKELETON_COUNT = 3

const formatAlertTime = (time: string) => {
  const parsed = moment(time, moment.ISO_8601, true)
  return parsed.isValid() ? parsed.fromNow() : time
}

// A full list fills the card; below this the spacing is left as designed.
const TIGHT_FROM_BULLETS = 3

const formatShortDate = (date: string) => {
  const parsed = moment(date, moment.ISO_8601, true)
  return parsed.isValid() ? parsed.format('MMM D') : date
}

interface ReviewVelocityProps {
  ProviderIcon?: React.FC<React.SVGProps<SVGSVGElement>> | null
  // The activity payload props are optional so callers can mount this section
  // while their query is still in flight and let it shimmer in place, rather
  // than withholding it until the data lands.
  alerts?: FacilityActivityAlert[]
  isLoading?: boolean
  platformActivity?: FacilityPlatformActivity
  providerName?: string
  spike?: FacilityActivitySpike | null
  velocity?: FacilityVelocityItem[]
}

export const ReviewVelocity: FC<ReviewVelocityProps> = ({
  alerts = [],
  isLoading = false,
  platformActivity,
  providerName = 'Tripadvisor',
  ProviderIcon = TripadvisorIcon,
  spike,
  velocity = [],
}) => {
  const [isSpikeModalOpen, setIsSpikeModalOpen] = useState(false)

  // First load only — once the activity payload is here a refetch keeps it.
  const isInitialLoading = isLoading && !platformActivity

  // The card's bullets, as a list so the layout can react to how many there
  // are. All three are present today; the count is read rather than assumed so
  // that a card showing fewer keeps its roomier spacing.
  const bullets = platformActivity
    ? [
        {
          key: 'per-day',
          node: (
            <>
              +{platformActivity.reviewsPerDay} reviews /{' '}
              <span className={styles.bulletBold}>day</span>
            </>
          ),
        },
        {
          key: 'peak-days',
          node: (
            <>
              Peak days:{' '}
              <span className={styles.bulletBold}>
                {platformActivity.peakDays.join(', ')}
              </span>
            </>
          ),
        },
        {
          key: 'spike',
          node: (
            <>
              Spike detected on{' '}
              <span className={styles.bulletBold}>
                {formatShortDate(platformActivity.spikeDate)}
              </span>
            </>
          ),
        },
      ]
    : []

  if (isInitialLoading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.velocityContainer}>
          <h3 className={styles.sectionHeader}>
            Review Velocity &amp; Dynamics
          </h3>
          <div className={styles.velocityBody}>
            {['Velocity', 'Platform Activity', 'Review spike detected']
              .slice(0, CARD_SKELETON_COUNT)
              .map((label) => (
                <div className={styles.subSection} key={label}>
                  <p className={styles.subSectionLabel}>{label}</p>
                  <Skeleton
                    height={CARD_HEIGHT}
                    radius={CARD_RADIUS}
                    width={CARD_WIDTH}
                  />
                </div>
              ))}
          </div>
        </div>

        <div className={styles.alertsContainer}>
          <h3 className={styles.sectionHeader}>Alerts</h3>
          <div className={styles.alertsList}>
            {Array.from({ length: ALERT_SKELETON_COUNT }).map((_, idx) => (
              <Skeleton
                height={ALERT_HEIGHT}
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                radius={ALERT_RADIUS}
                width={ALERT_WIDTH}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.velocityContainer}>
          <h3 className={styles.sectionHeader}>
            Review Velocity &amp; Dynamics
          </h3>

          <div className={styles.velocityBody}>
            {/* Velocity */}
            <div className={styles.subSection}>
              <p className={styles.subSectionLabel}>Velocity</p>
              <div className={styles.velocityCard}>
                {velocity.map((item, idx) => (
                  <div className={styles.velocityRow} key={idx}>
                    <div className={styles.velocityText}>
                      <span className={styles.velocityNumber}>
                        +{item.count}
                      </span>
                      <span className={styles.velocityUnit}>reviews / </span>
                      <span className={styles.velocityPeriod}>
                        {item.period}
                      </span>
                    </div>
                    <div className={styles.trendBadge}>
                      <div className={styles.trendIcon}>
                        <TrendUpIcon />
                      </div>
                      <span className={styles.trendValue}>{item.trend}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Platform Activity (absent only when there's genuinely no
                activity payload — loading is handled above) */}
            {platformActivity && (
              <div className={styles.subSection}>
                <p className={styles.subSectionLabel}>Platform Activity</p>
                <div
                  className={cn(
                    styles.platformCard,
                    // Only a full list needs the tighter spacing; at one or two
                    // bullets the card has room to spare and keeps the roomier
                    // rhythm the design draws.
                    bullets.length >= TIGHT_FROM_BULLETS &&
                      styles.platformCardTight
                  )}
                >
                  <div className={styles.platformHeader}>
                    {ProviderIcon && (
                      <div className={styles.platformIcon}>
                        <ProviderIcon />
                      </div>
                    )}
                    <p className={styles.platformName}>{providerName}</p>
                  </div>
                  <ul className={styles.bulletList}>
                    {bullets.map(({ key, node }) => (
                      <li className={styles.bulletItem} key={key}>
                        {node}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Review spike detected (spike is null when no recent activity) */}
            {spike && (
              <div className={styles.subSection}>
                <p className={styles.subSectionLabel}>Review spike detected</p>
                <div className={styles.spikeCard}>
                  <div className={styles.spikeContent}>
                    <div className={styles.spikeIconContainer}>
                      <WarningTriangleIcon
                        className={styles.spikeWarningIcon}
                      />
                    </div>
                    <div className={styles.spikeTextContainer}>
                      <p className={styles.spikeTitle}>
                        Spike detected{' '}
                        <span className={styles.spikeTitleBold}>
                          {formatShortDate(spike.date)}
                        </span>
                      </p>
                      <p className={styles.spikePercentage}>
                        <span className={styles.spikePercentageValue}>
                          +{spike.changePercent}%
                        </span>{' '}
                        reviews {spike.scope}
                      </p>
                    </div>
                  </div>
                  <button
                    className={styles.spikeViewDetails}
                    onClick={() => setIsSpikeModalOpen(true)}
                    type="button"
                  >
                    View Details →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Alerts */}
        <div className={styles.alertsContainer}>
          <h3 className={styles.sectionHeader}>Alerts</h3>

          <div className={styles.alertsList}>
            {alerts.map((alert, idx) => (
              <div className={styles.alertItem} key={idx}>
                <div className={styles.alertLeft}>
                  <div className={styles.alertIcon}>
                    <WarningTriangleIcon className={styles.alertWarningIcon} />
                  </div>
                  <span className={styles.alertLabel}>{alert.label}</span>
                </div>
                <span className={styles.alertTime}>
                  {formatAlertTime(alert.time)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <SpikeBreakdownModal
        isOpen={isSpikeModalOpen}
        onClose={() => setIsSpikeModalOpen(false)}
      />
    </>
  )
}
